/**
 * A labelled stacked bar chart: Vega-Lite bars plus an HTML key.
 *
 * Horizontal stacks with pill ends (left radius on the first segment, right
 * on the last). No grey track — that ink matches “no due date”. The key is
 * HTML rather than a Vega legend, matching {@link DonutChart}.
 */

import { DotIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';

import VegaChart, { type ChartSpec } from './VegaChart';
import Button from '@/components/ui/Button';

export type StackedBarSeries = {
  label: string;
  color: string;
};

export type StackedBarRow = {
  category: string;
  values: Record<string, number>;
};

type StackedBarChartProps = {
  series: readonly StackedBarSeries[];
  rows: readonly StackedBarRow[];
  ariaLabel: string;
  title?: string;
  emptyMessage?: string;
  /** Plot height in px. Bar thickness is capped so a single group stays a pill, not a block. */
  height?: number;
  className?: string;
  action?: { label: string; onClick: () => void };
  /** When set, every bar is scaled against this max (e.g. 100 for percentages). */
  xMax?: number;
  formatRowTotal?: (total: number) => string;
  showLegend?: boolean;
  tooltipCategoryTitle?: string;
  tooltipSeriesTitle?: string;
  tooltipValueTitle?: string;
};

const BAR_DATA = 'stackedBars';
const PLOT_WIDTH = 250;
const DEFAULT_PLOT_HEIGHT = 120;
const BAR_BAND = 0.85;
const MAX_BAR_HEIGHT = 36;
const BAR_RADIUS = 4;
const LABEL_GUTTER = 108;
const TOTAL_GUTTER = 18;
const FORMATTED_TOTAL_GUTTER = 36;

function rowTotal(row: StackedBarRow, series: readonly StackedBarSeries[]): number {
  return series.reduce((sum, item) => sum + (row.values[item.label] ?? 0), 0);
}

function stackedBarSpec(
  series: readonly StackedBarSeries[],
  rows: readonly StackedBarRow[],
  height: number,
  barHeight: number,
  xMax: number,
  tooltipCategoryTitle: string,
  tooltipSeriesTitle: string,
  tooltipValueTitle: string,
  formattedTotals: boolean
): ChartSpec {
  const labels = series.map((item) => item.label);
  const categories = rows.map((row) => row.category);
  const category = {
    field: 'category',
    type: 'nominal' as const,
    sort: categories,
    scale: { domain: categories, paddingInner: 0.18, paddingOuter: 0.06 },
    axis: {
      title: null,
      labels: false,
      ticks: false,
      domain: false,
      grid: false
    }
  };
  const xShared = {
    type: 'quantitative' as const,
    title: null,
    scale: { domain: [0, xMax], nice: false, zero: true },
    axis: { title: null, labels: false, ticks: false, domain: false, grid: false }
  };
  const tooltip = [
    { field: 'category', title: tooltipCategoryTitle },
    { field: 'series', title: tooltipSeriesTitle },
    { field: 'count', title: tooltipValueTitle, format: 'd' }
  ];
  const color = {
    field: 'series',
    type: 'nominal' as const,
    scale: { domain: labels, range: series.map((item) => item.color) },
    legend: null
  };
  const stackedBar = (kind: string, corners: Record<string, number>) => ({
    transform: [{ filter: `datum.kind === '${kind}'` }],
    mark: {
      type: 'bar',
      height: barHeight,
      tooltip: true,
      strokeWidth: 0,
      cornerRadiusTopLeft: corners.tl,
      cornerRadiusBottomLeft: corners.bl,
      cornerRadiusTopRight: corners.tr,
      cornerRadiusBottomRight: corners.br
    },
    encoding: {
      y: { ...category, axis: null },
      x: { ...xShared, field: 'x' },
      x2: { field: 'x2' },
      color,
      tooltip
    }
  });

  return {
    width: PLOT_WIDTH,
    height,
    padding: { top: 0, left: LABEL_GUTTER, right: formattedTotals ? FORMATTED_TOTAL_GUTTER : TOTAL_GUTTER, bottom: 0 },
    config: { padding: 0, view: { clip: false } },
    data: { name: BAR_DATA },
    resolve: { scale: { x: 'shared', y: 'shared' } },
    layer: [
      {
        transform: [{ filter: "datum.kind === 'track'" }],
        mark: {
          type: 'text',
          align: 'left',
          dx: -LABEL_GUTTER,
          baseline: 'middle',
          fontSize: 12,
          fill: '#334155',
          limit: LABEL_GUTTER - 4
        },
        encoding: {
          y: category,
          x: { ...xShared, datum: 0 },
          text: { field: 'category', type: 'nominal' }
        }
      },
      stackedBar('only', { tl: BAR_RADIUS, bl: BAR_RADIUS, tr: BAR_RADIUS, br: BAR_RADIUS }),
      stackedBar('first', { tl: BAR_RADIUS, bl: BAR_RADIUS, tr: 0, br: 0 }),
      stackedBar('mid', { tl: 0, bl: 0, tr: 0, br: 0 }),
      stackedBar('last', { tl: 0, bl: 0, tr: BAR_RADIUS, br: BAR_RADIUS }),
      {
        transform: [{ filter: "datum.kind === 'track' && datum.rowTotal === 0" }],
        mark: {
          type: 'bar',
          height: barHeight * 0.9,
          tooltip: false,
          fill: '#334155',
          cornerRadius: 6
        },
        encoding: {
          y: { ...category, axis: null },
          x: { ...xShared, datum: 0 },
          x2: { datum: Math.max(xMax / PLOT_WIDTH, xMax * 0.006) }
        }
      },
      {
        transform: [{ filter: "datum.kind === 'track'" }],
        mark: {
          type: 'text',
          align: 'left',
          dx: 4,
          baseline: 'middle',
          fontSize: 11,
          fill: '#334155',
          clip: false
        },
        encoding: {
          y: { ...category, axis: null },
          x: { ...xShared, field: 'rowTotal', axis: null },
          text: formattedTotals
            ? { field: 'rowTotalLabel', type: 'nominal' }
            : { field: 'rowTotal', type: 'quantitative' }
        }
      }
    ]
  };
}

export default function StackedBarChart({
  series,
  rows,
  ariaLabel,
  title,
  emptyMessage = 'Nothing to show',
  height = DEFAULT_PLOT_HEIGHT,
  className,
  action,
  xMax: xMaxOverride,
  formatRowTotal,
  showLegend = true,
  tooltipCategoryTitle = 'Group',
  tooltipSeriesTitle = 'Due',
  tooltipValueTitle = 'Actions'
}: StackedBarChartProps) {
  const barHeight = Math.min(MAX_BAR_HEIGHT, (height / Math.max(rows.length, 1)) * BAR_BAND);
  const xMax = xMaxOverride ?? Math.max(1, ...rows.map((row) => rowTotal(row, series)));
  const spec = useMemo(
    () =>
      stackedBarSpec(
        series,
        rows,
        height,
        barHeight,
        xMax,
        tooltipCategoryTitle,
        tooltipSeriesTitle,
        tooltipValueTitle,
        Boolean(formatRowTotal)
      ),
    [series, rows, height, barHeight, xMax, tooltipCategoryTitle, tooltipSeriesTitle, tooltipValueTitle, formatRowTotal]
  );

  const values = useMemo(
    () =>
      rows.flatMap((row) => {
        const total = rowTotal(row, series);
        const parts = series
          .map((item, rank) => ({ ...item, rank, count: row.values[item.label] ?? 0 }))
          .filter((item) => item.count > 0);
        const last = parts.length - 1;
        return [
          {
            kind: 'track',
            category: row.category,
            rowTotal: total,
            ...(formatRowTotal ? { rowTotalLabel: formatRowTotal(total) } : {})
          },
          ...parts.map((part, index) => {
            const start = parts.slice(0, index).reduce((sum, item) => sum + item.count, 0);
            const kind = parts.length === 1 ? 'only' : index === 0 ? 'first' : index === last ? 'last' : 'mid';
            return {
              kind,
              category: row.category,
              series: part.label,
              count: part.count,
              x: start,
              x2: start + part.count,
              rowTotal: total
            };
          })
        ];
      }),
    [rows, series, formatRowTotal]
  );

  const totals = useMemo(
    () =>
      series.map((item) => ({
        label: item.label,
        color: item.color,
        value: rows.reduce((sum, row) => sum + (row.values[item.label] ?? 0), 0)
      })),
    [rows, series]
  );

  return (
    <div
      className={`flex h-full flex-col justify-center gap-2 rounded-xl border-2 border-border-subtle px-3 py-3 ${className ?? ''}`}
    >
      {(title || action) && (
        <div className="flex items-center justify-between gap-2">
          {title ? <p className="text-sm font-semibold text-content-default">{title}</p> : <span />}
          {action ? (
            <Button variant="outline" size="sm" onClick={action.onClick}>
              {action.label}
            </Button>
          ) : null}
        </div>
      )}
      <div className="flex items-center gap-4">
        <div
          className="flex shrink-0 flex-col items-stretch justify-center overflow-visible"
          style={{
            width: PLOT_WIDTH + LABEL_GUTTER + (formatRowTotal ? FORMATTED_TOTAL_GUTTER : TOTAL_GUTTER)
          }}
        >
          {rows.length > 0 ? (
            <VegaChart spec={spec} ariaLabel={ariaLabel} dataName={BAR_DATA} values={values} />
          ) : (
            <div
              className="flex items-center justify-center text-center text-xs text-content-subtle"
              style={{ height }}
            >
              {emptyMessage}
            </div>
          )}
        </div>
        {showLegend ? (
          <div className="min-w-0 shrink-0 self-center pr-2">
            <ul className="space-y-1">
              {totals.map((item) => (
                <li key={item.label} className="flex items-center gap-2 text-xs text-content-default">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                    aria-hidden
                  />
                  <span className="inline-flex items-center">
                    {item.label}
                    <DotIcon size={14} weight="light" className="text-content-subtle" aria-hidden />
                    <span className="text-content-subtle">{item.value}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
